import { SURFACE_HEADER, type UsageSummaryDto } from '@filmsloth/shared';
import { currentSurface } from '../lib/usage.js';
import type {
  AccountDto,
  AuthStateDto,
  ContributionStatsDto,
  CreateSessionRequest,
  FilmSearchResultDto,
  FilterBreakdown,
  GenreId,
  HostDecision,
  MatchRule,
  ParticipantStateDto,
  ProviderId,
  ResultEntry,
  SessionCredentials,
  SessionFilters,
  SessionStateDto,
  TmdbAdminDto,
  TmdbSyncRequest,
  Trailer,
  Vote,
} from '@filmsloth/shared';

/** Same origin in development thanks to the Vite proxy; overridable for deploys. */
const BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Which shell this is running in — installed app or browser tab — so the
  // server's counters can tell the two apart for the actions it records
  // itself. It carries nothing about the person and decides nothing; see
  // `lib/usage.ts`.
  headers.set(SURFACE_HEADER, currentSurface());

  // The account cookie is httpOnly — it never touches this module directly —
  // so every request has to opt in to sending it, or the browser simply
  // withholds it and every `/auth/*` call looks like a signed-out visitor.
  const response = await fetch(`${BASE}/api${path}`, { ...init, headers, credentials: 'include' });
  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown_error',
      error?.message ?? 'Request failed',
    );
  }
  return payload as T;
}

export interface CatalogMeta {
  providers: { id: string; name: string; color: string }[];
  genres: { id: string; label: string }[];
  countries: string[];
  movieCount: number;
  runtimeRange: { min: number; max: number };
}

export interface SessionPreview {
  code: string;
  status: SessionStateDto['status'];
  hostName: string | null;
  participantCount: number;
  filters: SessionFilters;
}

export interface TrailerReportOutcome {
  sessions: number;
  threshold: number;
  removed: boolean;
  keptForReview: boolean;
}

/** `saved` = kept for later, `watched` = the group settled on it. */
export type WatchlistKind = 'saved' | 'watched';

export interface WatchlistEntry {
  movieId: string;
  title: string;
  kind: WatchlistKind;
  /** ISO timestamp. */
  addedAt: string;
}

export interface MovieRating {
  movieId: string;
  count: number;
  /** The plain mean, or null when nobody has rated it. */
  average: number | null;
  /** Pulled towards the middle while little is known; what ranking uses. */
  weighted: number | null;
  confident: boolean;
  /** What you gave it yourself, if anything. */
  yours: number | null;
}

export interface SessionSnapshot {
  state: SessionStateDto;
  personal: ParticipantStateDto;
}

export const api = {
  catalogMeta: () => request<CatalogMeta>('/catalog/meta'),

  /**
   * How many titles the filters yield, plus which filter is costing the
   * group its deck. The server also serves the bare count without
   * `breakdown=1`; nothing in the client needs it on its own.
   */
  describeFilters: (filters: SessionFilters) => {
    const params = new URLSearchParams({
      providers: filters.providers.join(','),
      genres: filters.genres.join(','),
      country: filters.country,
      breakdown: '1',
    });
    if (filters.excludedGenres?.length) {
      params.set('excludedGenres', filters.excludedGenres.join(','));
    }
    if (filters.maxRuntimeMinutes !== null) {
      params.set('maxRuntimeMinutes', String(filters.maxRuntimeMinutes));
    }
    if (filters.minYear != null) params.set('minYear', String(filters.minYear));
    if (filters.minRating != null) params.set('minRating', String(filters.minRating));
    return request<FilterBreakdown>(`/catalog/count?${params.toString()}`);
  },

  createSession: (body: CreateSessionRequest) =>
    request<{ credentials: SessionCredentials; state: SessionStateDto }>('/sessions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  previewSession: (code: string) =>
    request<SessionPreview>(`/sessions/${encodeURIComponent(code)}`),

  joinSession: (code: string, name: string) =>
    request<{ credentials: SessionCredentials; state: SessionStateDto }>(
      `/sessions/${encodeURIComponent(code)}/join`,
      { method: 'POST', body: JSON.stringify({ name }) },
    ),

  snapshot: (token: string) => request<SessionSnapshot>('/session', {}, token),

  updateFilters: (token: string, filters: SessionFilters) =>
    request<{ state: SessionStateDto }>(
      '/session/filters',
      { method: 'PATCH', body: JSON.stringify(filters) },
      token,
    ),

  start: (token: string) => request<SessionSnapshot>('/session/start', { method: 'POST' }, token),

  vote: (token: string, movieId: string, vote: Vote) =>
    request<SessionSnapshot>(
      '/session/votes',
      { method: 'POST', body: JSON.stringify({ movieId, vote }) },
      token,
    ),

  /**
   * Takes back your last vote. The response carries the movie that came
   * back, so the UI can say which one it was.
   */
  undoVote: (token: string) =>
    request<SessionSnapshot & { movieId: string }>('/session/votes', { method: 'DELETE' }, token),

  decide: (token: string, decision: HostDecision) =>
    request<{ state: SessionStateDto }>(
      '/session/decision',
      { method: 'POST', body: JSON.stringify({ decision }) },
      token,
    ),

  /**
   * Host only: sets how long the round may run, or `null` to take the clock
   * away. In the lobby this only records the choice; once people are swiping
   * it restarts the clock on the spot.
   */
  setTimer: (token: string, minutes: number | null) =>
    request<SessionSnapshot>(
      '/session/timer',
      { method: 'PUT', body: JSON.stringify({ minutes }) },
      token,
    ),

  /** Host only, lobby only: how agreeable a film has to be to match. */
  setMatchRule: (token: string, rule: MatchRule) =>
    request<SessionSnapshot>(
      '/session/match-rule',
      { method: 'PUT', body: JSON.stringify({ rule }) },
      token,
    ),

  /** Host only: the answer to a clock that has run out — more minutes. */
  extendTimer: (token: string, minutes: number) =>
    request<SessionSnapshot>(
      '/session/timer/extend',
      { method: 'POST', body: JSON.stringify({ minutes }) },
      token,
    ),

  /** Host only: opens the shortlist vote on a spent deck or an expired clock. */
  startRunoff: (token: string) =>
    request<SessionSnapshot>('/session/runoff', { method: 'POST' }, token),

  voteRunoff: (token: string, movieId: string) =>
    request<SessionSnapshot>(
      '/session/runoff/votes',
      { method: 'POST', body: JSON.stringify({ movieId }) },
      token,
    ),

  /** Host only: settles a tied shortlist vote. */
  breakRunoffTie: (token: string, movieId: string) =>
    request<SessionSnapshot>(
      '/session/runoff/tiebreak',
      { method: 'POST', body: JSON.stringify({ movieId }) },
      token,
    ),

  /** Puts an existing catalog title next up for everybody in the session. */
  /** Films in the catalog whose title contains this query, most popular first. */
  searchCatalog: (query: string) =>
    request<{ results: FilmSearchResultDto[] }>(`/catalog/search?q=${encodeURIComponent(query)}`),

  suggestExistingFilm: (token: string, movieId: string) =>
    request<SessionSnapshot>(
      '/session/suggest',
      { method: 'POST', body: JSON.stringify({ movieId }) },
      token,
    ),

  /** Suggests a brand-new title, and puts it next up for everybody. */
  suggestNewFilm: (
    token: string,
    input: {
      title: string;
      year?: number;
      runtimeMinutes?: number;
      genres?: GenreId[];
      providers?: ProviderId[];
    },
  ) =>
    request<SessionSnapshot>(
      '/session/suggest',
      { method: 'POST', body: JSON.stringify(input) },
      token,
    ),

  finish: (token: string) =>
    request<{ state: SessionStateDto }>('/session/finish', { method: 'POST' }, token),

  /** Host only: takes somebody out of the round — see `removeParticipant`. */
  removeParticipant: (token: string, participantId: string) =>
    request<SessionSnapshot>(
      `/session/participants/${encodeURIComponent(participantId)}`,
      { method: 'DELETE' },
      token,
    ),

  /** Host only: makes somebody else the host. */
  transferHost: (token: string, participantId: string) =>
    request<SessionSnapshot>(
      `/session/participants/${encodeURIComponent(participantId)}/host`,
      { method: 'POST' },
      token,
    ),

  /** Once a round is over: another one with the same filters, you as host. */
  startNextRound: (token: string) =>
    request<{ credentials: SessionCredentials; state: SessionStateDto }>(
      '/session/next-round',
      { method: 'POST' },
      token,
    ),

  leave: (token: string) => request<{ ok: true }>('/session/leave', { method: 'POST' }, token),

  /**
   * Signed-in only: drops a card from your own queue for the rest of this
   * session and records it against your account, so future sessions can
   * skip it too for as long as your settings say to.
   */
  /** Sends a card to the back of your own deck without voting on it. */
  deferCard: (token: string, movieId: string) =>
    request<SessionSnapshot>(
      '/session/later',
      { method: 'POST', body: JSON.stringify({ movieId }) },
      token,
    ),

  markSeen: (token: string, movieId: string) =>
    request<SessionSnapshot>(
      '/session/seen',
      { method: 'POST', body: JSON.stringify({ movieId }) },
      token,
    ),

  results: (token: string) => request<{ results: ResultEntry[] }>('/session/results', {}, token),

  /**
   * Proposes a trailer for a movie in the current session.
   *
   * Either it clears every check on the server and is live for the whole
   * group at once, or it comes back as an `ApiError` whose `code` says which
   * check it failed — see `trailerError` in the i18n dictionary.
   */
  submitTrailer: (token: string, movieId: string, url: string) =>
    request<{ trailer: Trailer }>(
      `/session/movies/${encodeURIComponent(movieId)}/trailer`,
      { method: 'POST', body: JSON.stringify({ url }) },
      token,
    ),

  /**
   * Rejects the trailer currently shown for a movie. It disappears for this
   * session either way; the response says how close it is to going for good.
   */
  reportTrailer: (token: string, movieId: string) =>
    request<TrailerReportOutcome>(
      `/session/movies/${encodeURIComponent(movieId)}/trailer-report`,
      { method: 'POST' },
      token,
    ),

  /** URL of the live event stream; the token travels as a query parameter. */
  eventsUrl: (token: string) => `${BASE}/api/session/events?token=${encodeURIComponent(token)}`,

  /* ------------------------------- watchlist ------------------------------ */

  watchlist: (kind?: WatchlistKind) =>
    request<{ entries: WatchlistEntry[] }>(`/watchlist${kind ? `?kind=${kind}` : ''}`),

  addToWatchlist: (input: { movieId: string; title: string; kind: WatchlistKind }) =>
    request<{ entry: WatchlistEntry }>('/watchlist', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  removeFromWatchlist: (movieId: string, kind: WatchlistKind) =>
    request<{ ok: true }>(`/watchlist/${encodeURIComponent(movieId)}?kind=${kind}`, {
      method: 'DELETE',
    }),

  /* -------------------------------- ratings ------------------------------- */

  ratings: (movieIds: string[]) =>
    request<{ ratings: MovieRating[] }>(
      `/ratings?movieIds=${encodeURIComponent(movieIds.join(','))}`,
    ),

  rate: (movieId: string, score: number) =>
    request<{ rating: MovieRating }>(`/ratings/${encodeURIComponent(movieId)}`, {
      method: 'PUT',
      body: JSON.stringify({ score }),
    }),

  clearRating: (movieId: string) =>
    request<{ rating: MovieRating }>(`/ratings/${encodeURIComponent(movieId)}`, {
      method: 'DELETE',
    }),

  /* --------------------------------- account ------------------------------- */

  register: (username: string, email: string, password: string) =>
    request<{ account: AccountDto }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    }),

  /** `identifier` is either the account's email or its username. */
  login: (identifier: string, password: string) =>
    request<{ account: AccountDto }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password }),
    }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  /**
   * Who, if anyone, this cookie belongs to. `{ account: null }` for a
   * visitor with no cookie is a normal, successful answer — not something
   * this call ever turns into a thrown error itself.
   */
  me: () => request<AuthStateDto>('/auth/me'),

  confirmEmail: (token: string) =>
    request<{ account: AccountDto }>('/auth/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  forgotPassword: (email: string) =>
    request<void>('/auth/password/forgot', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    request<{ account: AccountDto }>('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  /** Signed-in only: sets the streaming services and genres a new session defaults to. */
  updatePreferences: (preferredProviders: ProviderId[], preferredGenres: GenreId[]) =>
    request<{ account: AccountDto }>('/auth/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ preferredProviders, preferredGenres }),
    }),

  /** Ad-free days, the running ledger behind them, and the two contribution counts. */
  contributionStats: () => request<ContributionStatsDto>('/workshop/stats'),

  /** How long a film marked "already seen" should stay out of your deck. `null` = forever. */
  updateSeenExclusionDays: (seenExclusionDays: number | null) =>
    request<{ account: AccountDto }>('/auth/settings', {
      method: 'PATCH',
      body: JSON.stringify({ seenExclusionDays }),
    }),

  /**
   * Moderators only: how much the site is being used. Aggregate counters
   * plus the numbers read straight out of the tables — see
   * `server/src/stats/`. A 403 here is the normal answer for everybody else.
   */
  usageSummary: (days: number) => request<UsageSummaryDto>(`/stats/summary?days=${days}`),

  /** The TMDB sync: recent runs and how fresh the catalog is. Moderators only. */
  tmdbAdmin: () => request<TmdbAdminDto>('/admin/tmdb'),

  /** Starts a sync now. Answers before it finishes; poll `tmdbAdmin` to follow it. */
  startTmdbSync: (input: TmdbSyncRequest) =>
    request<{ started: boolean }>('/admin/tmdb/sync', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};
